// Swipe a card left with a finger to show a button behind it, as in iOS Mail
// (#386). Swiping right or tapping anywhere else shuts it. The page still
// scrolls up and down, and a touch that starts in a field is left alone, so
// typing is never a swipe. A mouse never swipes: the card's own buttons stay
// the way to do the same thing at a desk.
import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { swipeAxis, swipeOffset, swipeSettlesOpen, type SwipeAxis } from '../lib/swipe';

/** The width of what is revealed, in px (Tailwind w-24). */
const REVEAL_PX = 96;
const TYPING = 'input, textarea, select, [contenteditable="true"]';

interface Drag {
  x: number;
  y: number;
  from: number;
  axis: SwipeAxis;
}

interface Props {
  /** What the swipe reveals, stretched to fill a red strip REVEAL_PX wide at the card's right: make it the whole strip, so a tap anywhere on the red works (#386). */
  behind: ReactNode;
  children: ReactNode;
}

export function SwipeToReveal({ behind, children }: Props) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<Drag | null>(null);
  const swiped = useRef(false);
  const strip = useRef<HTMLDivElement>(null);
  const open = offset !== 0 && !dragging;

  // Open: a touch anywhere but the revealed button shuts it (the Remove question's own taps included).
  useEffect(() => {
    if (!open) return;
    const shut = (e: globalThis.PointerEvent) => {
      if (!strip.current?.contains(e.target as Node)) setOffset(0);
    };
    document.addEventListener('pointerdown', shut, true);
    return () => document.removeEventListener('pointerdown', shut, true);
  }, [open]);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    swiped.current = false;
    if (e.pointerType === 'mouse' || (e.target as Element).closest(TYPING)) return;
    drag.current = { x: e.clientX, y: e.clientY, from: offset, axis: null };
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (d.axis === null) {
      d.axis = swipeAxis(dx, e.clientY - d.y);
      if (d.axis === 'y') drag.current = null;
      if (d.axis !== 'x') return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    setOffset(swipeOffset(d.from, dx, REVEAL_PX));
  }

  function onPointerEnd() {
    if (drag.current?.axis === 'x') {
      swiped.current = true;
      setOffset((o) => (swipeSettlesOpen(o, REVEAL_PX) ? -REVEAL_PX : 0));
    }
    drag.current = null;
    setDragging(false);
  }

  return (
    <div className={`relative rounded-lg ${offset !== 0 ? 'overflow-hidden' : ''}`}>
      {/* Kept mounted (hidden while shut) so the question it opens survives the card sliding back. */}
      <div ref={strip} className={`absolute inset-y-0 right-0 flex w-24 items-stretch bg-red-600 ${offset === 0 ? 'invisible' : ''}`}>
        {behind}
      </div>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        // The tap that ends a swipe isn't also a tap on what it ended over (the open/close header).
        onClickCapture={(e) => {
          if (!swiped.current) return;
          swiped.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ transform: offset ? `translateX(${offset}px)` : undefined, touchAction: 'pan-y pinch-zoom' }}
        className={`relative ${dragging ? '' : 'transition-transform duration-200'}`}
      >
        {children}
      </div>
    </div>
  );
}
